import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Download, ExternalLink, Search, X } from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";
import BROCHURES, { getBrochurePdfUrl } from "@/lib/brochureData";

// -----------------------------------------------------------------------------
//  BrochuresPage — /brochures
//
//  A single-page hub for every Publuu flip-book brochure Grup Xaluca hosts.
//  The page is intentionally lean:
//    • Editorial header (overline · title · intro on 12-col, matches other
//      section headers).
//    • Real-time search: filters by full-or-partial hotel name against the
//      operator's current language. Trims + lowercases both sides for a
//      forgiving match.
//    • One responsive card per brochure. The card holds the brochure name,
//      an embedded Publuu iframe, and a discreet "Open in a new tab" link
//      so power users can pop the flip-book into a full window.
//
//  Adding a new brochure = adding a single object to `brochureData.js`.
// -----------------------------------------------------------------------------

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || obj.es || "";
};

const COPY = {
  overline: { en: "BROCHURES", fr: "CATALOGUES", es: "CATÁLOGOS" },
  title: {
    en: "Catalogues & Brochures",
    fr: "Catalogues & Brochures",
    es: "Catálogos y Folletos",
  },
  intro: {
    en: "Every property in a single library — leaf through the printed catalogue of each Grup Xaluca address, straight from your browser.",
    fr: "Toutes les adresses réunies en une seule bibliothèque — feuilletez le catalogue imprimé de chaque maison Grup Xaluca, directement depuis votre navigateur.",
    es: "Todos los alojamientos reunidos en una única biblioteca — hojea el catálogo impreso de cada dirección Grup Xaluca directamente desde tu navegador.",
  },
  searchPlaceholder: {
    en: "Search by accommodation name…",
    fr: "Rechercher par nom d'établissement…",
    es: "Buscar por nombre del alojamiento…",
  },
  count: {
    en: (n) => (n === 1 ? "1 brochure" : `${n} brochures`),
    fr: (n) => (n === 1 ? "1 catalogue" : `${n} catalogues`),
    es: (n) => (n === 1 ? "1 catálogo" : `${n} catálogos`),
  },
  clear: { en: "Clear search", fr: "Effacer", es: "Limpiar" },
  openTab: {
    en: "Open in a new tab",
    fr: "Ouvrir dans un nouvel onglet",
    es: "Abrir en una nueva pestaña",
  },
  viewHotel: {
    en: "See the property",
    fr: "Voir l'établissement",
    es: "Ver el alojamiento",
  },
  noResults: {
    en: "No brochure matches this search.",
    fr: "Aucun catalogue ne correspond à cette recherche.",
    es: "Ningún catálogo coincide con esta búsqueda.",
  },
  download: {
    en: "Download PDF",
    fr: "Télécharger le PDF",
    es: "Descargar catálogo",
  },
};

// ─────────────────────────────────────────────────────────────────────────
//  Brochure card
// ─────────────────────────────────────────────────────────────────────────
const BrochureCard = ({ brochure, lang }) => {
  const name = pick(brochure.name, lang);
  const pdfUrl = getBrochurePdfUrl(brochure);
  return (
    <article
      className="bg-white border border-[#E8DFCF] shadow-[0_4px_20px_-12px_rgba(44,38,33,0.15)] overflow-hidden flex flex-col"
      data-testid={`brochure-card-${brochure.id}`}
    >
      {/* Card header — hotel name + external-open link */}
      <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-[#E8DFCF]/70">
        <div className="min-w-0">
          <p className="overline text-[#C16542]">
            {COPY.overline[lang] || COPY.overline.en}
          </p>
          <h3
            className="font-serif-x mt-2 text-2xl md:text-3xl leading-tight text-[#2C2621] truncate"
            title={name}
          >
            {name}
          </h3>
        </div>
        <a
          href={brochure.publuuUrl.replace(/[?&]embed(&|$)/, "$1").replace(/&transparent/, "")}
          target="_blank"
          rel="noopener noreferrer"
          title={pick(COPY.openTab, lang)}
          className="shrink-0 inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-[#5C5248] hover:text-[#C16542] transition-colors"
          data-testid={`brochure-open-${brochure.id}`}
        >
          <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
        </a>
      </header>

      {/* Embedded flip-book. Publuu recommends a fixed height; we keep it
          `aspect-video`-adjacent so it scales gracefully on tablet/mobile
          without stretching the flip-book beyond its native ratio. */}
      <div className="relative bg-[#FDFBF7]">
        <iframe
          src={brochure.publuuUrl}
          title={name}
          scrolling="no"
          frameBorder="0"
          allow="clipboard-write; autoplay; fullscreen"
          allowFullScreen
          className="w-full block h-[320px] sm:h-[380px] md:h-[420px] lg:h-[460px]"
          data-testid={`brochure-iframe-${brochure.id}`}
          loading="lazy"
        />
      </div>

      {/* Card footer — link to the property detail page + PDF download. */}
      {(brochure.hotelSlug || pdfUrl) ? (
        <footer className="px-6 py-4 border-t border-[#E8DFCF]/70 flex items-center justify-between gap-4 flex-wrap">
          {brochure.hotelSlug ? (
            <Link
              to={`/hotels/${brochure.hotelSlug}`}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.24em] uppercase text-[#2C2621] hover:text-[#C16542] transition-colors"
              data-testid={`brochure-hotel-link-${brochure.id}`}
            >
              {pick(COPY.viewHotel, lang)} →
            </Link>
          ) : <span />}
          {pdfUrl ? (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.24em] uppercase text-[#5C5248] hover:text-[#C16542] transition-colors"
              data-testid={`brochure-download-${brochure.id}`}
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
              {pick(COPY.download, lang)}
            </a>
          ) : null}
        </footer>
      ) : null}
    </article>
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────────
const BrochuresPage = () => {
  const { lang } = useLanguage();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BROCHURES;
    return BROCHURES.filter((b) => {
      // Match against every language string so a French visitor searching
      // "sidi" still finds Aguelmame Sidi Ali even if the fr name diverges.
      const haystacks = [b.name?.en, b.name?.fr, b.name?.es, b.hotelSlug]
        .filter(Boolean)
        .map((s) => s.toLowerCase());
      return haystacks.some((h) => h.includes(q));
    });
  }, [query]);

  return (
    <div
      className="bg-[#FDFBF7] text-[#2C2621] pt-20 md:pt-24 pb-24 md:pb-32"
      data-testid="page-brochures"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Editorial header — matches the layout language of every other
            top-of-page hero on the site (12-col grid, overline · title
            · intro). */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-10 md:mb-14">
          <div className="md:col-span-5">
            <p className="overline text-[#C16542]">
              {COPY.overline[lang] || COPY.overline.en}
            </p>
            <h1 className="font-serif-x mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {pick(COPY.title, lang)}
            </h1>
          </div>
          <p className="md:col-span-6 md:col-start-7 self-end text-base md:text-lg text-[#5C5248] leading-relaxed">
            {pick(COPY.intro, lang)}
          </p>
        </div>

        {/* Search + count strip */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10 md:mb-14">
          <div className="relative sm:w-96">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A89A87]"
              strokeWidth={1.8}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={pick(COPY.searchPlaceholder, lang)}
              className="w-full pl-10 pr-10 py-3 bg-white border border-[#E8DFCF] focus:border-[#C16542] focus:outline-none text-sm placeholder:text-[#A89A87]"
              data-testid="brochures-search-input"
              aria-label={pick(COPY.searchPlaceholder, lang)}
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#5C5248] hover:text-[#C16542] transition-colors"
                title={pick(COPY.clear, lang)}
                data-testid="brochures-search-clear"
                aria-label={pick(COPY.clear, lang)}
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            ) : null}
          </div>
          <p
            className="text-[10px] tracking-[0.28em] uppercase text-[#5C5248]"
            data-testid="brochures-count"
          >
            {(COPY.count[lang] || COPY.count.en)(filtered.length)}
          </p>
        </div>

        {/* Grid or empty state */}
        {filtered.length === 0 ? (
          <div
            className="border border-dashed border-[#C16542]/40 bg-white p-10 md:p-16 text-center"
            data-testid="brochures-empty"
          >
            <BookOpen
              className="w-8 h-8 text-[#C16542] mx-auto mb-3"
              strokeWidth={1.5}
            />
            <p className="text-base text-[#2C2621] max-w-md mx-auto">
              {pick(COPY.noResults, lang)}
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 lg:gap-14"
            data-testid="brochures-grid"
          >
            {filtered.map((b) => (
              <BrochureCard key={b.id} brochure={b} lang={lang} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrochuresPage;
